/**
 * Choropleth color ramps — maps ramp names to d3-scale-chromatic interpolators
 */
import {
  interpolateBlues,
  interpolateReds,
  interpolateGreens,
  interpolateOranges,
  interpolatePurples,
  interpolateViridis,
  interpolatePlasma,
  interpolateYlOrRd,
  interpolateYlGnBu,
  interpolateRdYlGn,
  schemeSet2,
  schemeTableau10,
} from 'd3-scale-chromatic'
import { scaleSequential, scaleQuantize, scaleOrdinal, scaleThreshold } from 'd3-scale'

const SEQUENTIAL_RAMPS = {
  blues: interpolateBlues,
  reds: interpolateReds,
  greens: interpolateGreens,
  oranges: interpolateOranges,
  purples: interpolatePurples,
  viridis: interpolateViridis,
  plasma: interpolatePlasma,
  YlOrRd: interpolateYlOrRd,
  YlGnBu: interpolateYlGnBu,
  RdYlGn: interpolateRdYlGn,
}

const CATEGORICAL_PALETTES = {
  set2: schemeSet2,
  tableau10: schemeTableau10,
}

/**
 * Create a color scale for a given ramp name and domain
 * @param {string} rampName - Name of the color ramp
 * @param {string} scheme - 'sequential', 'diverging', or 'categorical'
 * @param {[number, number] | string[]} domain - Data domain
 * @param {object} [options] - Extra options
 * @param {number[]} [options.values] - All data values (enables quantile breaks)
 * @returns {{ getColor, getLegendStops }}
 */
export function createColorScale(rampName, scheme, domain, options = {}) {
  if (scheme === 'categorical') {
    return createCategoricalScale(domain)
  }

  const interpolator = SEQUENTIAL_RAMPS[rampName] || interpolateViridis
  const [min, max] = domain
  const numClasses = 5

  // Use quantile breaks when values are provided, otherwise equal-interval
  const thresholds =
    options.values?.length >= numClasses
      ? computeQuantileBreaks(options.values, numClasses)
      : computeCleanBreaks(min, max, numClasses)

  // Sample colors at the midpoint of each class
  const colors = []
  for (let i = 0; i < numClasses; i++) {
    const t = (i + 0.5) / numClasses
    colors.push(interpolator(t))
  }

  const scale = scaleThreshold().domain(thresholds).range(colors)

  return {
    getColor: (value) => {
      if (value == null || isNaN(value)) return [128, 128, 128, 100]
      const color = scale(value)
      return cssColorToRgba(color, 200)
    },
    getLegendStops: () => {
      const stops = []
      const breakpoints = [min, ...thresholds, max]
      // Cap iterations to available breakpoints to prevent out-of-bounds reads
      // when deduplication produces fewer thresholds than expected
      const maxStops = Math.min(numClasses, breakpoints.length - 1)
      for (let i = 0; i < maxStops; i++) {
        stops.push({
          min: breakpoints[i],
          max: breakpoints[i + 1],
          color: colors[i],
        })
      }
      return stops
    },
    thresholds,
    interpolator,
    domain: [min, max],
    type: 'classified',
  }
}

/**
 * Compute quantile breakpoints so each class contains roughly equal numbers of features.
 * Handles outliers gracefully — e.g., Mexico won't compress all Central American countries
 * into a single bucket.
 */
function computeQuantileBreaks(values, numClasses) {
  const sorted = values.filter((v) => v != null && !isNaN(v)).sort((a, b) => a - b)
  if (sorted.length < numClasses)
    return computeCleanBreaks(sorted[0], sorted[sorted.length - 1], numClasses)

  const thresholds = []
  for (let i = 1; i < numClasses; i++) {
    // Quantile position (e.g., for 5 classes: 0.2, 0.4, 0.6, 0.8)
    const idx = Math.floor((i / numClasses) * sorted.length)
    const raw = sorted[Math.min(idx, sorted.length - 1)]
    // Round to a clean number for readable legend labels
    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(Math.abs(raw), 1))))
    const roundTo = magnitude >= 1 ? magnitude : 1
    thresholds.push(Math.round(raw / roundTo) * roundTo)
  }

  // Remove duplicates and ensure strictly ascending
  const unique = [...new Set(thresholds)].sort((a, b) => a - b)
  return unique
}

/**
 * Compute clean, human-readable breakpoints for classified choropleth.
 * Divides the range into numClasses equal intervals, then rounds to clean numbers.
 */
function computeCleanBreaks(min, max, numClasses) {
  const range = max - min
  if (range === 0) return [min]

  const rawStep = range / numClasses

  // Determine rounding magnitude based on step size
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const roundTo = magnitude >= 1 ? magnitude : 1

  const thresholds = []
  for (let i = 1; i < numClasses; i++) {
    const raw = min + i * rawStep
    const rounded = Math.round(raw / roundTo) * roundTo
    thresholds.push(rounded)
  }

  // Remove duplicates and ensure strictly ascending
  const unique = [...new Set(thresholds)].sort((a, b) => a - b)
  return unique
}

/**
 * Create a categorical color scale
 */
function createCategoricalScale(categories) {
  const palette = schemeTableau10
  const scale = scaleOrdinal().domain(categories).range(palette)

  return {
    getColor: (value) => {
      if (value == null) return [128, 128, 128, 100]
      const color = scale(value)
      return cssColorToRgba(color, 200)
    },
    getLegendStops: () => {
      return categories.map((cat, i) => ({
        value: cat,
        color: palette[i % palette.length],
      }))
    },
    domain: categories,
  }
}

/**
 * Convert CSS color string to [r, g, b, a] array
 */
function cssColorToRgba(colorStr, alpha = 255) {
  if (!colorStr) return [128, 128, 128, alpha]

  // Handle rgb(r, g, b)
  const rgbMatch = colorStr.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (rgbMatch) {
    return [+rgbMatch[1], +rgbMatch[2], +rgbMatch[3], alpha]
  }

  // Handle hex
  if (colorStr.startsWith('#')) {
    const hex = colorStr.slice(1)
    if (hex.length === 3) {
      return [
        parseInt(hex[0] + hex[0], 16),
        parseInt(hex[1] + hex[1], 16),
        parseInt(hex[2] + hex[2], 16),
        alpha,
      ]
    }
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
      alpha,
    ]
  }

  return [128, 128, 128, alpha]
}

// Alias chains for common attribute name mismatches between
// Natural Earth properties and SQL-aliased DuckDB output columns.
const PROP_ALIASES = {
  pop_est: ['population', 'POP_EST'],
  POP_EST: ['population', 'pop_est'],
  population: ['pop_est', 'POP_EST'],
  gdp_md: ['GDP_MD', 'gdp', 'gdp_usd'],
  GDP_MD: ['gdp_md', 'gdp', 'gdp_usd'],
  name_en: ['name', 'NAME'],
  name: ['name_en', 'NAME'],
  pop_density: ['density_per_km2', 'pop_density_km2'],
}

/**
 * Get a property value case-insensitively, with alias fallback.
 */
function getPropertyCI(props, key) {
  if (!props) return undefined
  if (props[key] !== undefined) return props[key]
  const lower = key.toLowerCase()
  if (props[lower] !== undefined) return props[lower]
  const upper = key.toUpperCase()
  if (props[upper] !== undefined) return props[upper]
  // Try known aliases
  const aliases = PROP_ALIASES[key] || PROP_ALIASES[lower]
  if (aliases) {
    for (const alt of aliases) {
      if (props[alt] !== undefined) return props[alt]
    }
  }
  return undefined
}

/**
 * Get min and max values from a feature collection for a given attribute.
 * Case-insensitive property lookup handles both Natural Earth and DuckDB results.
 */
export function getDataExtent(features, attribute) {
  let min = Infinity
  let max = -Infinity
  for (const feature of features) {
    const val = getPropertyCI(feature.properties, attribute)
    if (val != null && !isNaN(val)) {
      if (val < min) min = val
      if (val > max) max = val
    }
  }
  return min === Infinity ? [0, 1] : [min, max]
}

/**
 * Get unique values from features for a given attribute.
 * Case-insensitive property lookup handles both Natural Earth and DuckDB results.
 */
export function getUniqueValues(features, attribute) {
  const values = new Set()
  for (const feature of features) {
    const val = getPropertyCI(feature.properties, attribute)
    if (val != null) values.add(val)
  }
  return [...values].sort()
}
